import { Autocomplete, Stack, TextField, TextFieldProps } from '@mui/material';
import { Theme } from '@mui/material/styles';
import type { InputHTMLAttributes } from 'react';
import { useEffect, useRef, useState } from 'react';

import { getPlaceDetails } from '@/api/places';

import type { AddressFormLine1 } from '@/types/form';
import type { PlacePrediction } from '@/types/places';

import { getDataTestId } from '@/utils/browser';
import { getStreetLineFromPlacesAutocomplete } from '@/utils/places';

import { Icon } from '../../Icon/Icon';

import { AutocompleteOption } from '../Select/Select.styled';

type AddressWithPlaceId = Extract<AddressFormLine1, object> & {
  place_id: string;
};

function getAddressWithPlaceId(
  value: AddressFormLine1 | undefined,
): AddressWithPlaceId | null {
  if (value && typeof value === 'object' && value.place_id) {
    return value as AddressWithPlaceId;
  }
  return null;
}

function inputValueAfterReset(
  structured: AddressWithPlaceId | null,
  next: string,
  predictionById: Record<string, PlacePrediction>,
  maxLength: number | undefined,
): string {
  if (structured && (structured.line1?.trim() || structured.line2)) {
    return structured.line1?.trim() || '';
  }
  const matchedSuggestion = Object.values(predictionById).find(
    prediction => prediction.description === next,
  );
  if (matchedSuggestion) {
    const interim = getStreetLineFromPlacesAutocomplete(matchedSuggestion);
    return maxLength ? interim.slice(0, maxLength) : interim;
  }
  return next;
}

function inputValueWhenSuggestionTextMatches(
  structured: AddressWithPlaceId | null,
  next: string,
  predictionById: Record<string, PlacePrediction>,
): string | null {
  if (!structured) return null;
  const suggestionDesc = predictionById[structured.place_id]?.description;
  if (
    suggestionDesc &&
    next === suggestionDesc &&
    next !== (structured.line1?.trim() || '')
  ) {
    return (structured.line1 && structured.line1.trim()) || '';
  }
  return null;
}

export type AddressAutocompleteFieldProps = {
  dataTestId: string;
  field: {
    value: AddressFormLine1 | undefined;
    onChange: (value: unknown) => void;
    onBlur: () => void;
  };
  maxLength?: number;
  inputProps?: InputHTMLAttributes<HTMLInputElement>;
  restTextFieldProps: Omit<TextFieldProps, 'inputProps'>;
  textFieldProps?: TextFieldProps;
  placeholder?: string;
  disabled?: boolean;
  shouldShowSuggestions: boolean;
  showSuggestions: () => void;
  hideSuggestions: () => void;
  predictionById: Record<string, PlacePrediction>;
  placeIds: string[];
  setSearchValue: (value: string) => void;
};

export function AddressAutocompleteField({
  dataTestId,
  field: { value, onChange, onBlur },
  maxLength,
  inputProps,
  restTextFieldProps,
  textFieldProps,
  placeholder,
  disabled,
  shouldShowSuggestions,
  showSuggestions,
  hideSuggestions,
  predictionById,
  placeIds,
  setSearchValue,
}: AddressAutocompleteFieldProps) {
  const [inputValue, setInputValue] = useState('');
  const valueRef = useRef(value);
  valueRef.current = value;

  useEffect(() => {
    if (value === '' || value === undefined || value === null) {
      setInputValue('');
      setSearchValue('');
      hideSuggestions();
      return;
    }

    if (typeof value === 'string') {
      setInputValue(value);
      return;
    }

    if (typeof value === 'object') {
      const placeId = value.place_id;
      const fromPrediction = placeId
        ? predictionById[placeId]?.description
        : '';

      setInputValue(
        (value.line1 && value.line1.trim()) ||
          value.description ||
          fromPrediction ||
          '',
      );
      return;
    }

    setInputValue('');
  }, [value, predictionById, setSearchValue, hideSuggestions]);

  const selectedValue =
    value && typeof value === 'object' && value.place_id
      ? value.place_id
      : typeof value === 'string'
        ? value
        : '';

  const extendedPlaceIds =
    typeof value === 'string' ? [value, ...placeIds] : placeIds;

  return (
    <Autocomplete<string>
      {...getDataTestId(dataTestId)}
      fullWidth
      getOptionLabel={placeId =>
        predictionById[placeId]?.description || placeId || ''
      }
      options={extendedPlaceIds}
      value={selectedValue}
      inputValue={inputValue}
      isOptionEqualToValue={(placeId, selectedPlaceId) =>
        placeId === selectedPlaceId
      }
      open={shouldShowSuggestions}
      noOptionsText="No locations"
      slotProps={{
        paper: {
          sx: theme => ({
            ...(!placeIds.length
              ? { display: 'none' }
              : {
                  border: `1px solid ${theme.palette.grey[200]}`,
                  padding: `${theme.spacing(2)} ${theme.spacing(1)}`,
                }),
          }),
        },
      }}
      onChange={async (_event, selectedPlaceId) => {
        const place = predictionById[selectedPlaceId || ''];

        if (place?.place_id) {
          const optimistic = getStreetLineFromPlacesAutocomplete(place);
          setInputValue(
            maxLength ? optimistic.slice(0, maxLength) : optimistic,
          );
          const addressObject = await getPlaceDetails(place.place_id);
          const line1Slice = (addressObject?.line1 || '').slice(0, maxLength);
          onChange({
            line1: line1Slice,
            line2: (addressObject?.line2 || '').slice(0, maxLength),
            city: (addressObject?.city || '').slice(0, maxLength),
            town: (addressObject?.town || '').slice(0, maxLength),
            poBoxDistrict: (addressObject?.poBoxDistrict || '').slice(
              0,
              maxLength,
            ),
            country: (addressObject?.country || '').slice(0, maxLength),
            zip: (addressObject?.zip || '').slice(0, maxLength),
            place_id: place.place_id,
            ...(line1Slice ? {} : { description: place.description }),
          });
        } else {
          onChange((place?.description || '').slice(0, maxLength));
        }

        hideSuggestions();
      }}
      onBlur={onBlur}
      onInputChange={(changeEvent, rawInputValue, reason) => {
        const isInputTarget = changeEvent?.currentTarget.tagName === 'INPUT';

        const next =
          !isInputTarget || !maxLength
            ? rawInputValue
            : rawInputValue.length > maxLength
              ? rawInputValue.slice(0, maxLength)
              : rawInputValue;

        const structured = getAddressWithPlaceId(valueRef.current);

        if (reason === 'reset') {
          setInputValue(
            inputValueAfterReset(structured, next, predictionById, maxLength),
          );
          return;
        }

        if (reason === 'input') {
          const fromSuggestionMatch = inputValueWhenSuggestionTextMatches(
            structured,
            next,
            predictionById,
          );
          if (fromSuggestionMatch !== null) {
            setInputValue(fromSuggestionMatch);
            return;
          }
        }

        setInputValue(next);
        if (reason === 'input' || reason === 'clear') {
          onChange({ target: { value: next } });
          setSearchValue(next);
        }
      }}
      onOpen={showSuggestions}
      onClose={hideSuggestions}
      renderInput={textFieldRenderParams => (
        <TextField
          {...textFieldRenderParams}
          placeholder={placeholder}
          fullWidth
          InputProps={{
            ...textFieldRenderParams.InputProps,
            endAdornment: null,
          }}
          inputProps={{
            ...textFieldRenderParams.inputProps,
            ...inputProps,
          }}
          {...restTextFieldProps}
          helperText={
            textFieldProps?.error ? (
              <Stack gap={0.5} component="span">
                <Icon
                  name="alert"
                  size={20}
                  color={(theme: Theme) => theme.palette.error.main}
                />
                {textFieldProps.helperText}
              </Stack>
            ) : (
              textFieldProps?.helperText
            )
          }
        />
      )}
      renderOption={(listItemProps, placeId) => {
        const prediction = predictionById[placeId];

        if (!prediction) return null;

        return (
          <AutocompleteOption {...listItemProps} key={placeId}>
            {prediction.description}
          </AutocompleteOption>
        );
      }}
      disabled={disabled}
    />
  );
}
